import { useState, type ReactNode } from 'react';
import { Alert, Button, Field, Input, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import type { Dashboard } from '@casa/model';
import type { ServerStore } from '../data/casa_server.ts';
import { DashboardError, loadFromServer, readSource, saveDashboard, SERVER_NAMES, suggestedName, type DashboardSource, type SaveTarget } from '../data/dashboard.ts';
import { isDashboardName } from '../data/device_store.ts';
import { availableStores, isServerStore, type StoreInfo, type WritableStore } from '../data/stores.ts';
import type { Draft } from './dashboard_edits.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';

// Saving edit mode's working copy. Save writes to the store the dashboard came from, without asking
// (docs/specs/settings.md, ST-2): this device, or the Tilez Hub over the version this device
// loaded; if another device saved it since, the user chooses: overwrite it, load theirs (losing these
// edits), or keep editing (S-5 in docs/specs/dashboard-editor.md). Only a dashboard from a web
// address, which is read-only, asks where to keep it (ST-8). "Save as" keeps a copy under another name
// in the same store.

export interface SavingOptions {
    bridge: Bridge | undefined;
    onSaved: (dashboard: Dashboard, notice: string) => void;
    /** The server's version was loaded instead of saving: the edits are gone. */
    onReloaded: (dashboard: Dashboard) => void;
    onFailed: (message: string) => void;
}

export interface Saving {
    save: (draft: Draft) => Promise<void>;
    saveAs: (draft: Draft) => void;
    overlay: ReactNode;
}

type Asking =
    /** A name, and a store when the dashboard comes from a read-only one. */
    | { kind: 'name'; title: string; why: string | undefined; draft: Draft; stores: StoreInfo[] }
    | { kind: 'changed'; draft: Draft; store: ServerStore; name: string; isDeleted: boolean };

/** What went wrong, in the dialog. */
function Problem({ text }: { text: string }) : ReactNode {
    return (
        <Alert.Root status="error" mt="3">
            <Alert.Indicator />
            <Alert.Title>{text}</Alert.Title>
        </Alert.Root>
    );
}

function messageOf(error: unknown) : string {
    const issues = error instanceof DashboardError && error.issues.length > 0 ? ` (${error.issues.join('; ')})` : '';

    return `${error instanceof Error ? error.message : String(error)}${issues}`;
}

const STORE_NAMES: Record<WritableStore, string> = { device: 'on this device', casa: 'on the Tilez Hub', cloud: 'on Tilez Cloud' };

/** Where a source keeps its dashboard, if it can be saved there. */
const writableOf = (source: DashboardSource | undefined) : WritableStore | undefined => (source?.kind === 'device' || source?.kind === 'casa' || source?.kind === 'cloud' ? source.kind : undefined);

function targetOf(store: WritableStore, name: string, isNew: boolean, source: DashboardSource | undefined) : SaveTarget {
    if (store === 'device') {
        return { kind: 'device', name, isNew };
    }
    // Over the version this device has; new when it has none (made here, not saved there yet).
    const condition = !isNew && source?.kind === store && source.version !== undefined ? { version: source.version } : ('new' as const);

    return { kind: 'server', store, name, condition };
}

export function useSaving({ bridge, onSaved, onReloaded, onFailed }: SavingOptions) : Saving {
    const [asking, setAsking] = useState<Asking>();
    const [name, setName] = useState('');
    const [store, setStore] = useState<WritableStore>('device');
    const [problem, setProblem] = useState<string>();
    const [isBusy, setBusy] = useState(false);

    const close = () : void => {
        setAsking(undefined);
        setProblem(undefined);
    };

    /** One save. Saved: the dialog closes; refused by the store: the user is asked; failed: said where it shows. */
    const attempt = async (draft: Draft, target: SaveTarget) : Promise<void> => {
        if (!bridge) {
            return;
        }

        setBusy(true);
        setProblem(undefined);
        try {
            const outcome = await saveDashboard(bridge, draft.dashboard, draft.origins, target);
            switch (outcome.status) {
                case 'saved':
                    close();
                    onSaved(outcome.dashboard, `Saved "${target.name}" ${STORE_NAMES[target.kind === 'device' ? 'device' : target.store]}.`);
                    break;
                case 'name taken':
                    if (asking?.kind === 'name') {
                        setProblem(`There is a dashboard called "${target.name}" ${STORE_NAMES[target.kind === 'device' ? 'device' : target.store]} already: choose another name.`);
                    } else {
                        onFailed(`Not saved: "${target.name}" is taken.`);
                    }
                    break;
                case 'changed elsewhere':
                    // No current version: deleted on the server since (its admin page).
                    setAsking({ kind: 'changed', draft, store: target.kind === 'server' ? target.store : 'casa', name: target.name, isDeleted: outcome.current === undefined });
                    break;
            }
        } catch (error) {
            if (asking) {
                setProblem(`Not saved: ${messageOf(error)}`);
            } else {
                onFailed(`Not saved: ${messageOf(error)}`);
            }
        } finally {
            setBusy(false);
        }
    };

    /** Asks for a name, and for a store when `source` is read-only. */
    const askName = async (draft: Draft, title: string, why: string | undefined, suggested: string, source: DashboardSource | undefined) : Promise<void> => {
        if (!bridge) {
            return;
        }

        const stores = (await availableStores(bridge)).filter((entry) => entry.isWritable);
        const own = writableOf(source);
        const first = stores[0]?.kind;
        setStore(own ?? (first !== undefined && isServerStore(first) ? first : 'device'));
        setName(suggested);
        setProblem(undefined);
        setAsking({ kind: 'name', title, why, draft, stores: own ? [] : stores });
    };

    const save = async (draft: Draft) : Promise<void> => {
        if (!bridge) {
            return;
        }

        const source = await readSource(bridge);
        const own = writableOf(source);
        if (own && source && source.kind !== 'url') {
            await attempt(draft, targetOf(own, source.name, false, source));

            return;
        }

        const url = source?.kind === 'url' ? source.url : undefined;
        const why = url ? `It came from ${url}, which can only be read.` : 'It has no place it is kept yet.';
        await askName(draft, 'Where to keep it', why, suggestedName(url ? new URL(url).pathname : undefined), source);
    };

    const saveAs = async (draft: Draft) : Promise<void> => {
        if (!bridge) {
            return;
        }

        await askName(draft, 'Save as a new dashboard', undefined, '', await readSource(bridge));
    };

    /** The server's version instead of this device's edits. */
    const loadTheirs = async (dashboardName: string, store: ServerStore) : Promise<void> => {
        if (!bridge) {
            return;
        }

        setBusy(true);
        try {
            const dashboard = await loadFromServer(bridge, dashboardName, store);
            close();
            onReloaded(dashboard);
        } catch (error) {
            setProblem(messageOf(error));
        } finally {
            setBusy(false);
        }
    };

    let overlay: ReactNode = null;
    if (asking?.kind === 'changed') {
        const { draft, isDeleted } = asking;
        overlay = (
            <ChromeDialog
                title={isDeleted ? 'Deleted on the Tilez Hub' : 'Changed on another device'}
                onClose={close}
                isQuestion
                footer={
                    <>
                        <Button variant="outline" onClick={close}>
                            Keep editing
                        </Button>
                        {isDeleted ? null : (
                            <Button variant="outline" loading={isBusy} onClick={() => void loadTheirs(asking.name, asking.store)}>
                                Load theirs, throw mine away
                            </Button>
                        )}
                        <Button colorPalette="red" loading={isBusy} onClick={() => void attempt(draft, { kind: 'server', store: asking.store, name: asking.name, condition: 'overwrite' })}>
                            {isDeleted ? 'Save it again' : 'Replace theirs with mine'}
                        </Button>
                    </>
                }
            >
                <Text>
                    {isDeleted
                        ? `"${asking.name}" was deleted from ${SERVER_NAMES[asking.store]} since this device loaded it. Saving puts this version back there.`
                        : `"${asking.name}" was saved on ${SERVER_NAMES[asking.store]} since this device loaded it, probably from another device. Saving now replaces that version with yours.`}
                </Text>
                {problem ? <Problem text={problem} /> : null}
            </ChromeDialog>
        );
    } else if (asking) {
        const { draft, stores } = asking;
        const isValidName = isDashboardName(name.trim());
        const confirm = async () : Promise<void> => {
            if (bridge) {
                await attempt(draft, targetOf(store, name.trim(), true, await readSource(bridge)));
            }
        };
        overlay = (
            <ChromeDialog
                title={asking.title}
                onClose={close}
                footer={
                    <>
                        <Button variant="outline" onClick={close}>
                            Keep editing
                        </Button>
                        <Button colorPalette="blue" disabled={!isValidName} loading={isBusy} onClick={() => void confirm()}>
                            Save
                        </Button>
                    </>
                }
            >
                <Stack gap="4">
                    {asking.why ? <Text>{asking.why}</Text> : null}
                    {stores.length > 1 ? (
                        <SettingsGroup title="Where to keep it">
                            {stores.map((entry) => (
                                <SettingsRow
                                    key={entry.kind}
                                    title={entry.title}
                                    detail={entry.detail}
                                    isCurrent={entry.kind === store}
                                    opens={false}
                                    onClick={() => setStore(isServerStore(entry.kind) ? entry.kind : 'device')}
                                />
                            ))}
                        </SettingsGroup>
                    ) : null}
                    <Field.Root invalid={name.trim() !== '' && !isValidName}>
                        <Field.Label>Name</Field.Label>
                        <Input aria-label="Name" autoFocus placeholder="A name, such as Kitchen tablet" value={name} onChange={(event) => setName(event.target.value)} />
                        <Field.ErrorText>Letters, digits, spaces, ".", "_" and "-", at most 64.</Field.ErrorText>
                    </Field.Root>
                    {problem ? <Problem text={problem} /> : null}
                </Stack>
            </ChromeDialog>
        );
    }

    return { save, saveAs: (draft) => void saveAs(draft), overlay };
}
